import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import ReactPlayer from 'react-player';
import styled from 'styled-components';
import { AppMainStateContext } from '../../contexts/AppMainStateContext';
import { useAppDispatch, useAppSelector } from '../../stores/store';
import { changeVideoAsync, playerActions } from './playerSlice';
import { usePlayerPage } from '../../hooks/usePlayerPage.tsx';
import Hls from 'hls.js';
import { useHlsDebug } from './debug/useHlsDebug.tsx';

const ReactPlayerWrapper = styled.div`
    @media screen and (min-width: 320px) and (max-width: 767px) and (orientation: portrait) {
        html {
            transform: rotate(-90deg);
            transform-origin: left top;
            width: 100vh;
            height: 100lvh;
            overflow-x: hidden;
            position: absolute;
            top: 100%;
            left: 0;
        }
    }
`;

const diagnoseError = async (url) => {
    try {
        await fetch(url, { method: 'HEAD', mode: 'no-cors' });
        // Jeśli doszliśmy tutaj, to znaczy, że fizycznie udało się "dotknąć" serwera.
        // Brak błędu typu TypeError oznacza, że sieć lokalna NIE jest blokowana.
        return 'SERVER_ISSUE';
    } catch (err) {
        if (err.name === 'TypeError') {
            // TypeError przy fetch do lokalnego IP to niemal zawsze blokada przeglądarki.
            return 'LOCAL_NETWORK_BLOCKED';
        }
        return 'UNKNOWN_NETWORK_ERROR';
    }
};

export const Player = () => {
    // const [timeoutId, setTimeoutId] = useState(0);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const [error, setError] = useState(false);
    const [retryCount, setRetryCount] = useState(0);
    const maxRetries = 3;
    const retryInterval = 3000; // 3 seconds
    // const hlsRef = useRef<Hls | null>(null);
    const dispatch = useAppDispatch();
    const { appStates } = useContext(AppMainStateContext);
    const { setHlsQualityLevels } = usePlayerPage();
    const playerState = useAppSelector((store) => store.player);
    // const actualPlayingChannelId = useAppSelector((store) => store.playerUI).actualChannelIndex;
    const playing = useAppSelector((store) => store.player.playing);
    const reactPlayerVideoRef = useRef<ReactPlayer>(null);
    const { isIOS } = useContext(AppMainStateContext);

    // const getAndSetQualityLevels = () => {
    //     const hlsSettings = reactPlayerVideoRef.current?.getInternalPlayer('hls');
    //     if (hlsSettings) {
    //         const hlsQualityLevels = reactPlayerVideoRef.current?.getInternalPlayer('hls').levels;
    //         setHlsQualityLevels(hlsQualityLevels);
    //         if (appStates.qualityLevelPreference === 'AUTO')
    //             dispatch(playerActions.setQualityLevel(-1));
    //         if (appStates.qualityLevelPreference === 'LOW')
    //             dispatch(playerActions.setQualityLevel(0));
    //         if (appStates.qualityLevelPreference === 'BEST')
    //             setTimeout(() => {
    //                 dispatch(playerActions.setQualityLevel(hlsQualityLevels.length - 1));
    //             }, 200);
    //     }
    // };

    useEffect(() => {
        return () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        };
    }, []);

    const syncQualityLevels = useCallback(() => {
        const hlsInstance = reactPlayerVideoRef.current?.getInternalPlayer('hls');
        if (hlsInstance) {
            const levels = hlsInstance.levels;
            setHlsQualityLevels(levels);

            let targetLevel = -1; // AUTO
            if (appStates.qualityLevelPreference === 'LOW') targetLevel = 0;
            if (appStates.qualityLevelPreference === 'BEST') targetLevel = levels.length - 1;

            dispatch(playerActions.setQualityLevel(targetLevel));
            hlsInstance.currentLevel = targetLevel;
        }
    }, [appStates.qualityLevelPreference, dispatch, setHlsQualityLevels]);

    useEffect(() => {
        const hlsInstance = reactPlayerVideoRef.current?.getInternalPlayer('hls');
        if (hlsInstance && hlsInstance.currentLevel !== playerState.qualityLevel) {
            hlsInstance.currentLevel = playerState.qualityLevel;
        }
    }, [playerState.qualityLevel]);

    const isMuted = playerState.muted || playerState.initialRun;

    const changeFocusOnOtherElem = () => {
        const markerBtn = document.getElementById('playerUITopBarMarkerBtn');
        if (markerBtn)
            markerBtn.focus();
    };

    // useEffect(() => {
    //     if (!hlsRef.current) {
    //         hlsRef.current = new Hls();
    //     }
    //     return () => {
    //         hlsRef.current?.destroy();
    //         hlsRef.current = null;
    //     };
    // }, []);

    // useEffect(() => {
    //     const hlsSettings = reactPlayerVideoRef.current?.getInternalPlayer('hls');
    //     if (hlsSettings) {
    //         setTimeout(() => {
    //             hlsSettings.nextLevel = playerState.qualityLevel;
    //         }, 200);
    //     }
    // }, [playerState.qualityLevel]);

    useEffect(() => {
        if (error && retryCount < maxRetries) {
            const timer = setTimeout(() => {
                setError(false);
                setRetryCount(prevCount => prevCount + 1);
            }, retryInterval);
            return () => clearTimeout(timer);
        }
    }, [error, retryCount]);

    useEffect(() => {
        setError(false);
        setRetryCount(0);
    }, [playerState.url]);

    const handlePlayerError = async (err: any, data: any) => {
        console.error('Player Error Data:', data);

        const errorType = await diagnoseError(playerState.url);
        console.log(errorType);
        if (errorType === 'LOCAL_NETWORK_BLOCKED') {
            dispatch(playerActions.setErrorStatus('Wymagana zgoda na dostęp do sieci lokalnej! <br><span class="text-sm">(Kliknij ikonę kłódki w pasku adresu i zezwól na „Urządzenia w sieci lokalnej”, aby odtworzyć materiał.)</span>'));
            dispatch(playerActions.setIsError(true));
            return;
        }

        if (data?.response?.code === 403 && playerState.actualPlayingChannel) {
            dispatch(changeVideoAsync({ chId: playerState.actualPlayingChannel.ch_id }));
            return;
        }

        if (retryCount < maxRetries) {
            timeoutRef.current = setTimeout(() => {
                setRetryCount(prev => prev + 1);
                setError(false);
            }, retryInterval);
        } else {
            dispatch(playerActions.setErrorStatus('Przekroczono limit prób połączenia'));
            dispatch(playerActions.setIsError(true));
            setError(true);
        }
    };

    useHlsDebug(reactPlayerVideoRef);

    return (
        <ReactPlayerWrapper
            className={'relative sm:max-h-screen sm:min-h-full sm:max-w-full aspect-video my-auto mx-auto'}>
            {!error &&
                <ReactPlayer
                    ref={reactPlayerVideoRef}
                    playing={playing}
                    url={playerState.url}
                    className={'react-player'}
                    config={{
                        file: {
                            forceHLS: !isIOS,
                            forceVideo: true,
                            attributes: {
                                controls: isIOS,
                                disablePictureInPicture: false,
                            },
                            hlsOptions: {
                                startLevel: -1,
                                debug: false,
                                manifestLoadingMaxRetry: 3,
                                levelLoadingMaxRetry: 3,
                                fragLoadingMaxRetry: 3,
                                keyLoadingMaxRetry: 0,
                            },
                        },
                    }}
                    pip={true}
                    muted={isMuted}
                    onReady={() => {
                        dispatch(playerActions.setPlaying(true));
                    }}
                    onPlay={() => {
                        dispatch(playerActions.setIsError(false));
                        if (retryCount !== 0)
                            setRetryCount(0);
                        changeFocusOnOtherElem();
                    }}
                    onStart={() => {
                        syncQualityLevels();
                        dispatch(playerActions.setIsError(false));
                    }}
                    onPause={() => {
                        dispatch(playerActions.setPlaying(false));
                    }}
                    onBuffer={() => dispatch(playerActions.setIsBuffer(true))}
                    onBufferEnd={() => {
                        // dispatch(playerActions.setIsError(false));
                        dispatch(playerActions.setIsBuffer(false));
                        dispatch(playerActions.setPlaying(true));
                    }}
                    // onDuration={() => {
                    //     dispatch(playerActions.setIsError(false));
                    // }}
                    // onProgress={() => {
                    //     dispatch(playerActions.setIsError(false));
                    //     if (retryCount !== 0)
                    //         setRetryCount(0);
                    //     if (timeoutId !== 0) {
                    //         clearTimeout(timeoutId);
                    //         setTimeoutId(0);
                    //     }
                    // }}
                    onError={handlePlayerError}
                    volume={playerState.volume}
                    height="100%"
                    width="100%"
                />
            }
        </ReactPlayerWrapper>
    );
};
